import { TailwindColorGroup } from 'tailwindcss/tailwind-config'

export type HostType =
  | 'sellematics'
  | 'sellmonitor'
  | 'uzum'
  | 'sellscreen'
  | 'psb'
  | 'vtb'
  | 'mayak'

export type TailwindColorName =
  | 'slate'
  | 'gray'
  | 'zinc'
  | 'neutral'
  | 'stone'
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'emerald'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'fuchsia'
  | 'pink'
  | 'rose'

export type YandexMetrica = {
  id: number
  options: { [key: string]: any }
}

export type FacbookMetrics = {
  id: number
}

export type ContactInformation = {
  phone?: string
  mail?: string
  telegram?: string
  maxSupport?: string
  instagram?: string
  whatsapp?: string
  wechat?: string
  youtube?: string
}

export type Theme = {
  hostType: HostType
  apiURL: string
  canonicalURL: string
  title: string
  releaseDate: number
  themeColors: TailwindColorName | TailwindColorGroup
  colorSeries: TailwindColorName[]
  logoDark: string
  logoFixed: string
  logoWhite: string
  logoFixedWhite: string
  chartFonts: string[]
  yandexMetrica?: YandexMetrica
  facebookMetrics?: FacbookMetrics
  contactInformation: ContactInformation
  notificationBots: Record<
    'development' | 'production',
    {
      telegram?: string
      max?: string
    }
  >
  notificationBot?: string
  maxNotificationBot?: string
  smsSender?: string
  extensionUrl?: string
}

const developmentNotificationBots = {
  telegram: 'test_sm_notify_bot',
  max: 'id7727492799_1_bot',
}

const SELLEMATICS: Theme = {
  hostType: 'sellematics',
  apiURL: process.env.NEXT_PUBLIC_SELLEMATICS_API_BASE_URL!,
  canonicalURL: 'https://sellematics.com',
  title: 'Sellematics',
  releaseDate: 2019,
  themeColors: 'orange',
  colorSeries: ['orange', 'teal', 'yellow', 'sky', 'red', 'violet', 'amber', 'blue', 'green'],
  logoDark: '/new/images/logo-dark.svg',
  logoFixed: '/new/images/logo-fixed.svg',
  logoWhite: '/new/images/logo-white.svg',
  logoFixedWhite: '/new/images/logo-fixed.svg',
  chartFonts: ['Roboto Flex', 'sans-serif'],
  yandexMetrica: {
    id: 64582036,
    options: {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
    },
  },
  contactInformation: {
    phone: '+74951485140',
    mail: 'info@sellematics.com',
    whatsapp: '+79800226979',
  },
  notificationBots: {
    development: developmentNotificationBots,
    production: {
      telegram: 'sellmonitor_notify_bot',
      max: 'id7727492799_bot',
    },
  },
  extensionUrl:
    'https://chromewebstore.google.com/detail/sellematics/bipdnjjhjoibnbijkcompcpmbchpdhgm',
}

const SELLMONITOR: Theme = {
  hostType: 'sellmonitor',
  apiURL: process.env.NEXT_PUBLIC_SELLMONITOR_API_BASE_URL!,
  canonicalURL: 'https://sellmonitor.com',
  title: 'Sellmonitor',
  releaseDate: 2019,
  themeColors: 'blue',
  colorSeries: ['blue', 'green', 'orange', 'lime', 'yellow', 'sky', 'red', 'violet', 'amber'],
  logoDark: '/theme/sellmonitor/images/logo-dark.svg',
  logoFixed: '/theme/sellmonitor/images/logo-fixed.svg',
  logoWhite: '/theme/sellmonitor/images/logo-white.svg',
  logoFixedWhite: '/theme/sellmonitor/images/logo-fixed-white.svg',
  chartFonts: ['Roboto Flex', 'sans-serif'],
  yandexMetrica: {
    id: 76206847,
    options: {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      webvisor: true,
    },
  },
  contactInformation: {
    phone: '+74951827510',
    mail: 'info@sellmonitor.com',
    telegram: 'SellmonitorHELP_bot',
    maxSupport: 'https://max.ru/id7727492799_2_bot',
    instagram: 'sellmonitor_com',
    youtube: 'Sellmonitor',
  },
  notificationBots: {
    development: developmentNotificationBots,
    production: {
      telegram: 'sellmonitor_notify_bot',
      max: 'id7727492799_bot',
    },
  },
  smsSender: 'Sellmonitor',
  extensionUrl:
    'https://chromewebstore.google.com/detail/sellmonitor/nlldhlipbgolknjpcihjdobjjngeejag',
}

const UZUM: Theme = {
  hostType: 'uzum',
  apiURL: process.env.NEXT_PUBLIC_UZUM_API_BASE_URL!,
  canonicalURL: 'https://uzum.sellmonitor.com',
  title: 'Sellmonitor',
  releaseDate: 2019,
  themeColors: 'blue',
  colorSeries: ['blue', 'green', 'orange', 'lime', 'yellow', 'sky', 'red', 'violet', 'amber'],
  logoDark: '/theme/sellmonitor/images/logo-dark.svg',
  logoFixed: '/theme/sellmonitor/images/logo-fixed.svg',
  logoWhite: '/theme/sellmonitor/images/logo-white.svg',
  logoFixedWhite: '/theme/sellmonitor/images/logo-fixed-white.svg',
  chartFonts: ['Roboto Flex', 'sans-serif'],
  yandexMetrica: {
    id: 76206847,
    options: {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      webvisor: true,
    },
  },
  contactInformation: {
    phone: '+74951485140',
    mail: 'info@sellmonitor.com',
    telegram: 'SellmonitorHELP_bot',
    instagram: 'sellmonitor_com',
    youtube: 'Sellmonitor',
  },
  notificationBots: {
    development: developmentNotificationBots,
    production: {},
  },
  smsSender: 'Sellmonitor',
}

const SELLSCREEN: Theme = {
  hostType: 'sellscreen',
  apiURL: process.env.NEXT_PUBLIC_SELLSCREEN_API_BASE_URL!,
  canonicalURL: 'https://sellscreen.io',
  title: 'Sellscreen',
  releaseDate: 2019,
  themeColors: 'blue',
  colorSeries: ['blue', 'green', 'orange', 'lime', 'yellow', 'sky', 'red', 'violet', 'amber'],
  logoDark: '/next/sellscreen/images/logo-dark.svg',
  logoFixed: '/next/sellscreen/images/logo-fixed.svg',
  logoWhite: '',
  logoFixedWhite: '/next/sellscreen/images/logo-fixed.svg',
  chartFonts: ['Roboto Flex', 'sans-serif'],
  yandexMetrica: {
    id: 89200218,
    options: {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      webvisor: true,
    },
  },
  facebookMetrics: {
    id: 1319899998823877,
  },
  contactInformation: {
    wechat: 'saioukf',
  },
  notificationBots: {
    development: developmentNotificationBots,
    production: {},
  },
  extensionUrl:
    'https://chromewebstore.google.com/detail/sellscreen/kbcjhdbnfdinccjhdcicdmpflamjflok',
}

const PSB: Theme = {
  hostType: 'psb',
  apiURL: process.env.NEXT_PUBLIC_PSB_API_BASE_URL!,
  canonicalURL: 'https://psb.analytics.pvcd.ru',
  title: 'ПСБ Аналитика на маркетплейсах',
  releaseDate: 2025,
  themeColors: {
    50: '#EEEFFB',
    100: '#DEDEF7',
    200: '#BDBEF0',
    300: '#9C9DE8',
    400: '#7A7CE0',
    500: '#595BD9',
    600: '#383AD1',
    700: '#2527A2',
    800: '#191A6C',
    900: '#0C0D36',
    950: '#07071D',
  },
  colorSeries: ['blue', 'green', 'orange', 'lime', 'yellow', 'sky', 'red', 'violet', 'amber'],
  logoDark: '/next/psb/images/logo-dark.svg',
  logoFixed: '/next/psb/images/logo-fixed.svg',
  logoWhite: '/next/psb/images/logo-white.svg',
  logoFixedWhite: '/next/psb/images/logo-fixed.svg',
  chartFonts: ['Roboto Flex', 'sans-serif'],
  yandexMetrica: {
    id: 101986889,
    options: {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
    },
  },
  contactInformation: {},
  notificationBots: {
    development: developmentNotificationBots,
    production: { telegram: 'mall_analytics_notify_bot' },
  },
}

const VTB: Theme = {
  hostType: 'vtb',
  apiURL: process.env.NEXT_PUBLIC_VTB_API_BASE_URL!,
  canonicalURL: 'https://vtb.sellmonitor.com',
  title: 'ВТБ-Селлер',
  releaseDate: 2025,
  themeColors: {
    50: '#eef6ff',
    100: '#d9eafc',
    200: '#bfdbf8',
    300: '#8ec4f9',
    400: '#5ab0ff',
    500: '#0084ff',
    600: '#3261ec',
    700: '#1e4bd2',
    800: '#0a2896',
    900: '#102974',
    950: '#0b1c49',
  },
  colorSeries: ['blue', 'green', 'orange', 'lime', 'yellow', 'sky', 'red', 'violet', 'amber'],
  logoDark: '/next/vtb/images/logo-dark.svg',
  logoFixed: '/next/vtb/images/logo-fixed.svg',
  logoWhite: '/next/vtb/images/logo-white.svg',
  logoFixedWhite: '/next/vtb/images/logo-fixed.svg',
  chartFonts: ['Roboto Flex', 'sans-serif'],
  yandexMetrica: {
    id: 103419887,
    options: {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
    },
  },
  contactInformation: {},
  notificationBots: {
    development: developmentNotificationBots,
    production: { telegram: 'vtbseller_notify_bot' },
  },
  extensionUrl: 'https://chromewebstore.google.com/detail/fcjkfebjiohcodeanfjojppdihmhhmkp',
}

const MAYAK: Theme = {
  hostType: 'mayak',
  apiURL: process.env.NEXT_PUBLIC_MAYAK_API_BASE_URL!,
  canonicalURL: 'https://new.mayak.bz',
  title: 'Маяк',
  releaseDate: 2020,
  themeColors: 'blue',
  colorSeries: ['blue', 'yellow', 'violet', 'green', 'sky', 'red', 'orange', 'amber', 'teal'],
  logoDark: '/next/mayak/images/logo-dark.svg',
  logoFixed: '/next/mayak/images/logo-fixed.svg',
  logoWhite: '/next/mayak/images/logo-white.svg',
  logoFixedWhite: '/next/mayak/images/logo-fixed.svg',
  chartFonts: ['Roboto Flex', 'sans-serif'],
  yandexMetrica: {
    id: 66634765,
    options: {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      webvisor: true,
    },
  },
  contactInformation: {
    phone: '+79253022949',
    mail: 'hello@mayak.bz',
    telegram: 'MayakHelp_Bot',
    youtube: 'channel/UCtBmoLyBXScmob5lMpCrgig',
  },
  notificationBots: {
    development: {
      telegram: 'dev_mayak_bz_ai_bot',
      max: 'id9727124044_bot',
    },
    production: {
      telegram: 'mayak_bz_ai_bot',
      max: 'id1683009223_3_bot',
    },
  },
  smsSender: 'MDataTek',
  extensionUrl:
    'https://chromewebstore.google.com/detail/%D0%BC%D0%B0%D1%8F%D0%BA-%D0%B1%D0%B5%D1%81%D0%BF%D0%BB%D0%B0%D1%82%D0%BD%D0%B0%D1%8F-%D0%B0%D0%BD%D0%B0%D0%BB%D0%B8%D1%82%D0%B8%D0%BA%D0%B0/iobjoojnnfjhlfmihckakpcgljgpmnih?hl=ru&pli=1',
}

const DEFAULT: Theme = SELLMONITOR

export const ThemesByHostType: Record<HostType, Theme> = {
  sellematics: SELLEMATICS,
  sellmonitor: SELLMONITOR,
  uzum: UZUM,
  sellscreen: SELLSCREEN,
  psb: PSB,
  vtb: VTB,
  mayak: MAYAK,
}

export const SelectableThemes = Object.values(ThemesByHostType)

export const getAssistantName = (theme: Theme) =>
  theme.hostType === 'vtb' ? 'ВТБ-Селлер GPT' : `${theme.title}GPT`

export const Themes = { SELLEMATICS, SELLMONITOR, SELLSCREEN, UZUM, PSB, VTB, MAYAK, DEFAULT }
